/*
* Copyright (c) 2024 SGS Europe. All rights reserved.
*
* This program is the CONFIDENTIAL and PROPRIETARY property of SGS Europe.
* Any unauthorised use, reproduction, or transfer of this program is strictly
* prohibited.
*
* File Name: GreenlightCanvasZoom.js
*
* Description: Contains the code for the Canvas Zoom Functionality.
*
*/

(function () {

	"use strict";

	/**
	 * Canvas Zoom Object.
	 * @param {object} oCanvas - The canvas to perform zooms on.
	 */
	function CanvasZoom(oCanvas) {

		// The canvas object to perform zooms on.
		this.g_oCanvas = oCanvas;

		// The multipler to zoom in and out by.
		this.g_iZoomMultiplier = getZoomMultiplierForZoomIncrement(g_oSettings.g_oUserSettings.ZoomIncrements);

		// The minimum and maximum zoom level.
		this.c_iMinimumZoomLevel = 1;
		this.c_iMaximumZoomLevel = 100;

		// Scale settings for zoom slider.
		this.c_iMinSliderPos = 0;
		this.c_iMaxSliderPos = 100;
		this.c_iMinSliderZoomVal = Math.log(100);
		this.c_iMaxSliderZoomVal = Math.log(10000);
		this.c_iExpoAdjustmentScale = (this.c_iMaxSliderZoomVal - this.c_iMinSliderZoomVal) / (this.c_iMaxSliderPos - this.c_iMinSliderPos);

		// Enable zoom warnings.
		this.displayMaximumZoomInWarning = true;
		this.displayMinimumZoomOutWarning = false;

		// If we should log any zoom steps.
		this.g_bLogZoom = false;
	};

	/**
	 * Performs a zoom.
	 */
	CanvasZoom.prototype.zoomIn = function () {
		
		// Check the maximum zoom level has not been reached.
		if (!this.canZoomIn()) {
			return;
		}

		// Get the zoom level.
		this.g_oCanvas.g_iTempZoomLevel = this.g_oCanvas.g_iZoomLevel * this.g_iZoomMultiplier;

		// If the zoom level would be greater then the maximum, set this to the maximum.
		if (this.g_oCanvas.g_iTempZoomLevel > this.c_iMaximumZoomLevel) {
			this.g_oCanvas.g_iTempZoomLevel = this.c_iMaximumZoomLevel;
		}

		// Calculate the zoom.
		this.g_oCanvas.g_iZoomDiff = (this.g_oCanvas.g_iTempZoomLevel - this.g_oCanvas.g_iZoomLevel) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iTempDragOffset_x = this.g_oCanvas.g_iDragOffset_x * (this.g_oCanvas.g_iTempZoomLevel / this.g_oCanvas.g_iZoomLevel);
		this.g_oCanvas.g_iTempDragOffset_y = this.g_oCanvas.g_iDragOffset_y * (this.g_oCanvas.g_iTempZoomLevel / this.g_oCanvas.g_iZoomLevel);
		this.g_oCanvas.g_iDragXDiff = (this.g_oCanvas.g_iTempDragOffset_x - this.g_oCanvas.g_iDragOffset_x) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iDragYDiff = (this.g_oCanvas.g_iTempDragOffset_y - this.g_oCanvas.g_iDragOffset_y) / this.g_oCanvas.g_iAnimateFrames;

		// Animate the zoom.
		this.startZoomAnimate();
	}

	/**
	 * Performs a zoom out.
	 */
	CanvasZoom.prototype.zoomOut = function () {

		// Check if we can zoom out.
		if (!this.canZoomOut()) {
			return;
		}

		// Get the zoom level.
		this.g_oCanvas.g_iTempZoomLevel = this.g_oCanvas.g_iZoomLevel / this.g_iZoomMultiplier;

		// If the zoom level would be less then the minimum, set this to the minimum.
		if (this.g_oCanvas.g_iTempZoomLevel < this.c_iMinimumZoomLevel) {
			this.g_oCanvas.g_iTempZoomLevel = this.c_iMinimumZoomLevel;
		}

		// Calculate the zoom.
		this.g_oCanvas.g_iZoomDiff = (this.g_oCanvas.g_iZoomLevel - this.g_oCanvas.g_iTempZoomLevel) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iTempDragOffset_x = this.g_oCanvas.g_iDragOffset_x / (this.g_oCanvas.g_iZoomLevel / this.g_oCanvas.g_iTempZoomLevel);
		this.g_oCanvas.g_iTempDragOffset_y = this.g_oCanvas.g_iDragOffset_y / (this.g_oCanvas.g_iZoomLevel / this.g_oCanvas.g_iTempZoomLevel);
		this.g_oCanvas.g_iDragXDiff = (this.g_oCanvas.g_iDragOffset_x - this.g_oCanvas.g_iTempDragOffset_x) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iDragYDiff = (this.g_oCanvas.g_iDragOffset_y - this.g_oCanvas.g_iTempDragOffset_y) / this.g_oCanvas.g_iAnimateFrames;

		// Animate the zoom.
		this.startZoomAnimate();
	}

	/**
	 * Checks if the canvas can be zoomed in.
	 */
	CanvasZoom.prototype.canZoomIn = function () {

		// Display a zoom warning if required.
		if (this.displayMaximumZoomInWarning && this.g_oCanvas.g_iZoomLevel >= this.c_iMaximumZoomLevel && $(".bubbleWarning").find("span").contents().text().indexOf(c_szMaximumZoomLevelReached) == -1) {
			g_oGreenlightMessages.displayWarningBubble(c_szMaximumZoomLevelReached);
		}

		// Determine if a zoom in can be performed.
		return this.g_oCanvas.g_iZoomLevel < this.c_iMaximumZoomLevel;
	};


	/**
	 * Checks if the canvas can be zoomed out.
	 */
	CanvasZoom.prototype.canZoomOut = function () {

		// Display a zoom warning if required.
		if (this.displayMinimumZoomOutWarning && this.g_oCanvas.g_iZoomLevel <= this.c_iMinimumZoomLevel && $(".bubbleWarning").find("span").contents().text().indexOf(c_szMinimumZoomLevelReached) == -1) {
			g_oGreenlightMessages.displayWarningBubble(c_szMinimumZoomLevelReached);
		}

		// Determine if a zoom out can be performed.
		return this.g_oCanvas.g_iZoomLevel > this.c_iMinimumZoomLevel;
	};

	/**
	 * Checks if the canvas can be zoom animated.
	 */
	CanvasZoom.prototype.canZoomAnimate = function () {
		if ((Math.abs(this.g_oCanvas.g_iTempZoomLevel - this.g_oCanvas.g_iZoomLevel) > 0) ||
			(Math.abs(this.g_oCanvas.g_iTempDragOffset_x - this.g_oCanvas.g_iDragOffset_x) > 1) ||
			(Math.abs(this.g_oCanvas.g_iTempDragOffset_y - this.g_oCanvas.g_iDragOffset_y) > 1)) {
			return true;
		}
		else {
			return false;
		}
	};

	/**
	 * Performs a zoom reset.
	 */
	CanvasZoom.prototype.zoomReset = function () {

		// Reset the zoom level.
		this.g_oCanvas.g_iTempZoomLevel = 1;
		this.g_oCanvas.g_iTempDragOffset_x = 0;
		this.g_oCanvas.g_iTempDragOffset_y = 0;

		// Animate to the reset zoom level.
		this.g_oCanvas.g_iZoomDiff = (this.g_oCanvas.g_iZoomLevel - this.g_oCanvas.g_iTempZoomLevel) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iDragXDiff = (this.g_oCanvas.g_iDragOffset_x - this.g_oCanvas.g_iTempDragOffset_x) / this.g_oCanvas.g_iAnimateFrames;
		this.g_oCanvas.g_iDragYDiff = (this.g_oCanvas.g_iDragOffset_y - this.g_oCanvas.g_iTempDragOffset_y) / this.g_oCanvas.g_iAnimateFrames;
		this.startZoomAnimate();

		// Clear any real-time render image and remove any image loading shine.
		this.g_oCanvas.clearRealtimeRenderImage();
		$(".shine").fadeOut(200);
	}

	/**
	 * Performs a zoom slide.
	 * @param {int} sliderPercentage - The slider percentage.
	 */
	CanvasZoom.prototype.zoomSlide = function (sliderPercentage) {

		// Get the zoom level.
		this.g_oCanvas.g_iTempZoomLevel = this.getZoomLevelFromSliderPercentage(sliderPercentage);
		var iZoomLevel = 0;

		// Calculate the zoom.
		if (this.g_oCanvas.g_iTempZoomLevel > this.g_oCanvas.g_iZoomLevel) {
			this.g_oCanvas.g_iZoomDiff = (this.g_oCanvas.g_iTempZoomLevel - this.g_oCanvas.g_iZoomLevel) / this.g_oCanvas.g_iAnimateFrames;
			iZoomLevel = this.g_oCanvas.g_iTempZoomLevel / this.g_oCanvas.g_iZoomLevel;
			this.g_oCanvas.g_iTempDragOffset_x = this.g_oCanvas.g_iDragOffset_x * iZoomLevel;
			this.g_oCanvas.g_iTempDragOffset_y = this.g_oCanvas.g_iDragOffset_y * iZoomLevel;
			this.g_oCanvas.g_iDragXDiff = (this.g_oCanvas.g_iTempDragOffset_x - this.g_oCanvas.g_iDragOffset_x) / this.g_oCanvas.g_iAnimateFrames;
			this.g_oCanvas.g_iDragYDiff = (this.g_oCanvas.g_iTempDragOffset_y - this.g_oCanvas.g_iDragOffset_y) / this.g_oCanvas.g_iAnimateFrames;
		}
		else {
			this.g_oCanvas.g_iZoomDiff = (this.g_oCanvas.g_iZoomLevel - this.g_oCanvas.g_iTempZoomLevel) / this.g_oCanvas.g_iAnimateFrames;
			iZoomLevel = (this.g_oCanvas.g_iZoomLevel / this.g_oCanvas.g_iTempZoomLevel);
			this.g_oCanvas.g_iTempDragOffset_x = this.g_oCanvas.g_iDragOffset_x / iZoomLevel;
			this.g_oCanvas.g_iTempDragOffset_y = this.g_oCanvas.g_iDragOffset_y / iZoomLevel;
			this.g_oCanvas.g_iDragXDiff = (this.g_oCanvas.g_iDragOffset_x - this.g_oCanvas.g_iTempDragOffset_x) / this.g_oCanvas.g_iAnimateFrames;
			this.g_oCanvas.g_iDragYDiff = (this.g_oCanvas.g_iDragOffset_y - this.g_oCanvas.g_iTempDragOffset_y) / this.g_oCanvas.g_iAnimateFrames;
		}

		// Animate the zoom.
		this.startZoomAnimate();

		// Since we're getting a new image, clear the (if any) real-time rendered image.
		this.g_oCanvas.clearRealtimeRenderImage();
	}

	/**
	 * Updates the zoom slider.
	 */
	CanvasZoom.prototype.updateZoomSlider = function () {
		$("#divNavSlider_Range").val(this.getSliderPercentageFromZoomLevel(Math.round(this.g_oCanvas.g_iZoomLevel * 100)));
		$("#divNavSlider_Label").html(Math.round(this.g_oCanvas.g_iZoomLevel * 100) + "%");
	};

	/**
	 * Gets the zoom level from the slider percentage.
	 * @param {int} sliderPercentage - The slider percentage.
	 */
	CanvasZoom.prototype.getZoomLevelFromSliderPercentage = function (sliderPercentage) {
		return Math.exp(this.c_iMinSliderZoomVal + this.c_iExpoAdjustmentScale * (sliderPercentage - this.c_iMinSliderPos)) / 100;
	}

	/**
	 * Gets the slider percentgae from the zoom level.
	 * @param {any} zoomLevel - The zoom level.
	 */
	CanvasZoom.prototype.getSliderPercentageFromZoomLevel = function (zoomLevel) {
		return this.c_iMinSliderPos + (Math.log(zoomLevel) - this.c_iMinSliderZoomVal) / this.c_iExpoAdjustmentScale;
	}

	/**
	 * Performs a marquee zoom.
	 * @param {array} oSyncCanvas - Any sync canvas.
	 */
	CanvasZoom.prototype.marqueeZoom = function (oSyncCanvas) {
		var _this = this;
		$("#" + _this.g_oCanvas.g_oAnnotationCanvas.g_szCanvasId).css('cursor', 'crosshair');
		g_oAnnotations.g_szPrevCursorStyle = "crosshair";
		changeCanvasFunction($("#" + _this.g_oCanvas.g_oAnnotationCanvas.g_szCanvasId), 'pointerup');
		changeCanvasFunction($("#" + _this.g_oCanvas.g_oAnnotationCanvas.g_szCanvasId), 'click');
		changeCanvasFunction($("#" + _this.g_oCanvas.g_oAnnotationCanvas.g_szCanvasId), 'pointerdown', function (event) {
			g_oMarquee.initiateMarqueeDraw(event, eAnnotationTypes.Rectangle, _this.g_oCanvas.g_oAnnotationCanvas, _this.g_oCanvas, oSyncCanvas, null, _this.marqueZoomPointerUp, true, false, null, false);
		});
	};

	/**
	 * The marquee pointer up event for zooming.
	 * @param {object} event - The event.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {array} oSyncCanvas - Any sync canvas.
	 * @param {int} iBeginPoint_x - The start x point.
	 * @param {int} iBeginPoint_y - The start y point.
	 * @param {int} iEndPoint_x - The end x point.
	 * @param {int} iEndPoint_y - The end y point.
	 */
	CanvasZoom.prototype.marqueZoomPointerUp = function (event, oArtworkViewer, oSyncCanvas, iBeginPoint_x, iBeginPoint_y, iEndPoint_x, iEndPoint_y) {

		// Check if the marquee has been drawn within the artwork.
		if (!g_oMarquee.isMarqueeWithinArtwork(oArtworkViewer)) {

			// Clear the marquee.
			oArtworkViewer.redraw(true);
			return;
		}

		// Check the maximum zoom level has not been reached.
		if (!oArtworkViewer.g_oCanvasZoom.canZoomIn()) {
			oArtworkViewer.redraw(true);
			return;
		}

		// We are going to do a marquee zoom.
		// Find the centre of the dragged area in px.
		var centrePoint_x = (iEndPoint_x - iBeginPoint_x) / 2 + iBeginPoint_x;
		var centerPoint_y = (iEndPoint_y - iBeginPoint_y) / 2 + iBeginPoint_y;

		// Find the offset amount in px from centre.
		oArtworkViewer.g_iTempDragOffset_x += centrePoint_x - ((oArtworkViewer.g_oCanvas.width / 2) - oArtworkViewer.g_iDragOffset_x);
		oArtworkViewer.g_iTempDragOffset_y += centerPoint_y - ((oArtworkViewer.g_oCanvas.height / 2) - oArtworkViewer.g_iDragOffset_y);
		
		// Determine the zoom level for the width of the marquee:
		// 1 - Determine the number of pixels in the width of the marquee.
		// 2 - Determine the number of pixels in the width of the canvas.
		// 3 - Determine the zoom level we need to fit the marquee area to the width of the canvas.
		var marqueePixelsX = Math.abs(g_oMarquee.g_iOrigin_x - g_oMarquee.g_iX);
		var canvasPixelsX = g_oViewer.g_oArtworkViewer.g_oCanvas.width * oArtworkViewer.g_iZoomLevel;
		var zoomLevelWidth = canvasPixelsX / marqueePixelsX;

		// Determine the zoom level for the height of the marquee:
		// 1 - Determine the number of pixels in the height of the marquee.
		// 2 - Determine the number of pixels in the height of the canvas.
		// 3 - Determine the zoom level we need to fit the marquee area to the height of the canvas.
		var marqueePixelsY = Math.abs(g_oMarquee.g_iOrigin_y - g_oMarquee.g_iY);
		var canvasPixelsY = g_oViewer.g_oArtworkViewer.g_oCanvas.height * oArtworkViewer.g_iZoomLevel;
		var zoomLevelHeight = canvasPixelsY / marqueePixelsY;

		// Determine the zoom level to be used based on the width and height of the area we wanted to see in the marquee.
		oArtworkViewer.g_iTempZoomLevel = Math.min(zoomLevelWidth, zoomLevelHeight);

		// If the zoom level would be greater then the maximum, set this to the maximum.
		if (oArtworkViewer.g_iTempZoomLevel > oArtworkViewer.g_oCanvasZoom.c_iMaximumZoomLevel) {
			oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_oCanvasZoom.c_iMaximumZoomLevel;
		}

		// Determine the amount we need to zoom, and the drag offset amount, based on the current zoom level.
		var iZoomAmount = (oArtworkViewer.g_iTempZoomLevel / oArtworkViewer.g_iZoomLevel);
		oArtworkViewer.g_iTempDragOffset_x *= iZoomAmount;
		oArtworkViewer.g_iTempDragOffset_y *= iZoomAmount;

		// Calulate the zoom and drag offset difference by frame.
		oArtworkViewer.g_iZoomDiff = (oArtworkViewer.g_iTempZoomLevel - oArtworkViewer.g_iZoomLevel) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragXDiff = (oArtworkViewer.g_iTempDragOffset_x - oArtworkViewer.g_iDragOffset_x) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragYDiff = (oArtworkViewer.g_iTempDragOffset_y - oArtworkViewer.g_iDragOffset_y) / oArtworkViewer.g_iAnimateFrames;

		// Ensure the zoom level has changed.
		if (oArtworkViewer.g_iZoomDiff == 0) {

			// Clear the marquee.
			oArtworkViewer.redraw(true);
			return;
		}

		// If we are syncing the zoom across other canvas.
		for (var i = 0; i < oSyncCanvas.length; i++) {
			if (oSyncCanvas[i].g_szCanvasId != oArtworkViewer.g_szCanvasId) {
				oSyncCanvas[i].synchroniseZoomPanProperties(oArtworkViewer);
				oSyncCanvas[i].g_oCanvasZoom.startZoomAnimate();
			}
		}

		// Zoom the canvas.
		oArtworkViewer.g_oCanvasZoom.startZoomAnimate();
	};

	/**
	 * Starts a zoom animation.
	 */
	CanvasZoom.prototype.startZoomAnimate = function () {
		this.zoomAnimate(1);
	}

	/**
	 * Performs the frames for a zoom animation.
	 * @param {int} iCurrentAnimationFrame - The current animation frame.
	 */
	CanvasZoom.prototype.zoomAnimate = function (iCurrentAnimationFrame) {
		
		// Check that there is a valid zoom or translate to make.
		if (this.canZoomAnimate() && iCurrentAnimationFrame <= this.g_oCanvas.g_iAnimateFrames) {

			// If we are logging the zoom steps.
			if (this.g_bLogZoom) {
				console.log(
					"Zoom: (" + this.g_oCanvas.g_iZoomLevel + " => " + this.g_oCanvas.g_iTempZoomLevel + ") " + this.g_oCanvas.g_iZoomDiff + ". " +
					"X: (" + this.g_oCanvas.g_iDragOffset_x + " => " + this.g_oCanvas.g_iTempDragOffset_x + ") " + this.g_oCanvas.g_iDragXDiff + ". " +
					"Y: (" + this.g_oCanvas.g_iDragOffset_y + " => " + this.g_oCanvas.g_iTempDragOffset_y + ") " + this.g_oCanvas.g_iDragYDiff + "."
				);
			}

			// Set that we are animating the canvas.
			this.g_oCanvas.g_bIsAnimating = true;

			// Clear any existing image data.
			this.g_oCanvas.clearImgData();

			// Request the next animation frame.
			this.g_iCurrentZoomFrame++;
			requestAnimationFrame(this.zoomAnimate.bind(this, ++iCurrentAnimationFrame));

			// If we need to zoom in.
			if (this.g_oCanvas.g_iTempZoomLevel > this.g_oCanvas.g_iZoomLevel) {

				// Determine the next zoom level.
				this.g_oCanvas.g_iZoomLevel = addFloats(this.g_oCanvas.g_iZoomLevel, this.g_oCanvas.g_iZoomDiff);
				this.g_oCanvas.g_iDragOffset_x = addFloats(this.g_oCanvas.g_iDragOffset_x, this.g_oCanvas.g_iDragXDiff);
				this.g_oCanvas.g_iDragOffset_y = addFloats(this.g_oCanvas.g_iDragOffset_y, this.g_oCanvas.g_iDragYDiff);
			}
			// If we need to zoom out.
			else {

				// Determine the next zoom level.
				this.g_oCanvas.g_iZoomLevel = subtractFloats(this.g_oCanvas.g_iZoomLevel, this.g_oCanvas.g_iZoomDiff);
				this.g_oCanvas.g_iDragOffset_x = subtractFloats(this.g_oCanvas.g_iDragOffset_x, this.g_oCanvas.g_iDragXDiff);
				this.g_oCanvas.g_iDragOffset_y = subtractFloats(this.g_oCanvas.g_iDragOffset_y, this.g_oCanvas.g_iDragYDiff);

				// If we have no zooming or dragging left, end the zoom animate.
				if (this.g_oCanvas.g_iZoomDiff == 0 && this.g_oCanvas.g_iDragXDiff == 0 && this.g_oCanvas.g_iDragYDiff == 0) {
					this.endZoomAnimate();
				}
			}
		}
		// End the zoom animate.
		else {
			this.endZoomAnimate();
		}

		// Update the zoom slider.
		if (this.g_oCanvas.g_bMainCanvas) {
			this.updateZoomSlider();
		}

		// Redraw the canvas.
		this.g_oCanvas.redraw(true);
	}

	/**
	 * Ends a zoom animation.
	 */
	CanvasZoom.prototype.endZoomAnimate = function () {

		// End the zoom animate.
		this.g_oCanvas.g_iDragOffset_x = this.g_oCanvas.g_iTempDragOffset_x;
		this.g_oCanvas.g_iDragOffset_y = this.g_oCanvas.g_iTempDragOffset_y;
		this.g_oCanvas.g_iZoomLevel = this.g_oCanvas.g_iTempZoomLevel;
		this.g_oCanvas.g_iDragXDiff = 0;
		this.g_oCanvas.g_iDragYDiff = 0;
		this.g_oCanvas.g_iZoomDiff = 0;
		this.g_oCanvas.g_bIsAnimating = false;
	}

	/**
	 * Performs a scroll zoom.
	 * @param {object} event - The scroll event.
	 * @param {array} oSyncCanvas - The sync canvas.
	 * @param {eWheelZoomType} scrollType - The scroll type.
	 */
	CanvasZoom.prototype.scrollZoom = function (event, oSyncCanvas, scrollType) {

		// Check we can zoom in.
		if (!this.canZoomIn()) {
			return;
		}

		// Determine the scroll type.
		switch (scrollType) {

			// Static zoom.
			case eWheelZoomType.Static:

				// Zoom in each sync canvas.
				for (var i = 0; i < oSyncCanvas.length; i++) {
					oSyncCanvas[i].g_bIsDragging = false;
					oSyncCanvas[i].g_oCanvasZoom.zoomIn();
				}

				// Zoom in this canvas.
				this.zoomIn();
				break;

			// Marquee based zooms.
			case eWheelZoomType.Centered:
			case eWheelZoomType.Smart:

				// Initiate the marque based zoom.
				g_oMarquee.initiateMarqueeWheelZoom(event, this.g_oCanvas.g_oAnnotationCanvas, this.g_oCanvas, oSyncCanvas, this.scrollZoomInCallback, scrollType);
				break;
		};
	}

	/**
	 * Performs a scroll zoom out.
	 * @param {object} event - The scroll event.
	 * @param {array} oSyncCanvas - The sync canvas.
	 * @param {eWheelZoomType} scrollType - The scroll type.
	 */
	CanvasZoom.prototype.scrollZoomOut = function (event, oSyncCanvas, scrollType) {

		// Check we can zoom out.
		if (!this.canZoomOut()) {
			return;
		}

		// Determine the scroll type.
		switch (scrollType) {

			// Static zoom.
			case eWheelZoomType.Static:

				// Zoom out each sync canvas.
				for (var i = 0; i < oSyncCanvas.length; i++) {
					oSyncCanvas[i].g_bIsDragging = false;
					oSyncCanvas[i].g_oCanvasZoom.zoomOut();
				}

				// Zoom out this canvas.
				this.zoomOut();
				break;

			// Marquee based zooms.
			case eWheelZoomType.Centered:
			case eWheelZoomType.Smart:

				// Initiate the marque based zoom out.
				g_oMarquee.initiateMarqueeWheelZoomOut(event, this.g_oCanvas.g_oAnnotationCanvas, this.g_oCanvas, oSyncCanvas, scrollType == eWheelZoomType.Centered ? this.scrollZoomOutCenteredCallback : this.scrollZoomOutCallback, scrollType);
				break;
		};
	}

	/**
	 * The callback for a scroll zoom in event.
	 * @param {object} event - The event.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {array} oSyncCanvas - Any sync canvas.
	 * @param {int} iBeginPoint_x - The start x point.
	 * @param {int} iBeginPoint_y - The start y point.
	 * @param {int} iEndPoint_x - The end x point.
	 * @param {int} iEndPoint_y - The end y point.
	 */
	CanvasZoom.prototype.scrollZoomInCallback = function (event, oArtworkViewer, oSyncCanvas, iBeginPoint_x, iBeginPoint_y, iEndPoint_x, iEndPoint_y) {

		// Calculate the zoom amount.
		oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_iZoomLevel * oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;

		// If we are over our maximum zoom level, set this to the maximum.
		if (oArtworkViewer.g_iTempZoomLevel > oArtworkViewer.g_oCanvasZoom.c_iMaximumZoomLevel) {
			oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_oCanvasZoom.c_iMaximumZoomLevel;
		}

		// Find the centre of the area in px.
		var centrePoint_x = (iEndPoint_x - iBeginPoint_x) / 2 + iBeginPoint_x;
		var centerPoint_y = (iEndPoint_y - iBeginPoint_y) / 2 + iBeginPoint_y;

		// Find the offset amount in px from centre.
		oArtworkViewer.g_iTempDragOffset_x += centrePoint_x - ((oArtworkViewer.g_oCanvas.width / 2) - oArtworkViewer.g_iDragOffset_x);
		oArtworkViewer.g_iTempDragOffset_y += centerPoint_y - ((oArtworkViewer.g_oCanvas.height / 2) - oArtworkViewer.g_iDragOffset_y);

		// Ensure the scroll will be in the bounds of the image.
		oArtworkViewer.g_iTempDragOffset_x = oArtworkViewer.g_iTempDragOffset_x > oArtworkViewer.g_iCompareImage_x / 2 ? oArtworkViewer.g_iCompareImage_x / 2 : oArtworkViewer.g_iTempDragOffset_x;
		oArtworkViewer.g_iTempDragOffset_x = oArtworkViewer.g_iTempDragOffset_x < -oArtworkViewer.g_iCompareImage_x / 2 ? -oArtworkViewer.g_iCompareImage_x / 2 : oArtworkViewer.g_iTempDragOffset_x;
		oArtworkViewer.g_iTempDragOffset_y = oArtworkViewer.g_iTempDragOffset_y > oArtworkViewer.g_iCompareImage_y / 2 ? oArtworkViewer.g_iCompareImage_y / 2 : oArtworkViewer.g_iTempDragOffset_y;
		oArtworkViewer.g_iTempDragOffset_y = oArtworkViewer.g_iTempDragOffset_y < -oArtworkViewer.g_iCompareImage_y / 2 ? -oArtworkViewer.g_iCompareImage_y / 2 : oArtworkViewer.g_iTempDragOffset_y;

		// Modify the offset by the zoom amount.
		var iZoomAmount = (oArtworkViewer.g_iTempZoomLevel / oArtworkViewer.g_iZoomLevel);
		oArtworkViewer.g_iTempDragOffset_x *= iZoomAmount;
		oArtworkViewer.g_iTempDragOffset_y *= iZoomAmount;

		// Calculate the zoom and drag offset difference by frame.
		oArtworkViewer.g_iZoomDiff = (oArtworkViewer.g_iTempZoomLevel - oArtworkViewer.g_iZoomLevel) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragXDiff = (oArtworkViewer.g_iTempDragOffset_x - oArtworkViewer.g_iDragOffset_x) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragYDiff = (oArtworkViewer.g_iTempDragOffset_y - oArtworkViewer.g_iDragOffset_y) / oArtworkViewer.g_iAnimateFrames;

		// If we are syncing the zoom across other canvas.
		for (var i = 0; i < oSyncCanvas.length; i++) {
			if (oSyncCanvas[i].g_szCanvasId != oArtworkViewer.g_szCanvasId) {
				oSyncCanvas[i].synchroniseZoomPanProperties(oArtworkViewer);
				oSyncCanvas[i].g_oCanvasZoom.startZoomAnimate();
			}
		}

		// Zoom the canvas.
		oArtworkViewer.g_oCanvasZoom.startZoomAnimate();
	};

	/**
	 * The callback for a scroll zoom out event.
	 * @param {object} event - The event.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {array} oSyncCanvas - Any sync canvas.
	 * @param {int} iBeginPoint_x - The start x point.
	 * @param {int} iBeginPoint_y - The start y point.
	 * @param {int} iEndPoint_x - The end x point.
	 * @param {int} iEndPoint_y - The end y point.
	 */
	CanvasZoom.prototype.scrollZoomOutCallback = function (event, oArtworkViewer, oSyncCanvas, iBeginPoint_x, iBeginPoint_y, iEndPoint_x, iEndPoint_y) {

		// Calculate the zoom amount.
		oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_iZoomLevel / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;

		// If we are over our minimum zoom level, set this to the minimum.
		if (oArtworkViewer.g_iTempZoomLevel < oArtworkViewer.g_oCanvasZoom.c_iMinimumZoomLevel) {
			oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_oCanvasZoom.c_iMinimumZoomLevel;
		}

		// Find the centre of the area in px.
		var centrePoint_x = (iEndPoint_x - iBeginPoint_x) / 2 + iBeginPoint_x;
		var centerPoint_y = (iEndPoint_y - iBeginPoint_y) / 2 + iBeginPoint_y;

		// Find the offset amount in px from centre.
		oArtworkViewer.g_iTempDragOffset_x += centrePoint_x - ((oArtworkViewer.g_oCanvas.width / 2) - oArtworkViewer.g_iDragOffset_x);
		oArtworkViewer.g_iTempDragOffset_y += centerPoint_y - ((oArtworkViewer.g_oCanvas.height / 2) - oArtworkViewer.g_iDragOffset_y);

		// Ensure the scroll will be in the bounds of the image.
		oArtworkViewer.g_iTempDragOffset_x = oArtworkViewer.g_iTempDragOffset_x > oArtworkViewer.g_iCompareImage_x / 2 ? oArtworkViewer.g_iCompareImage_x / 2 : oArtworkViewer.g_iTempDragOffset_x;
		oArtworkViewer.g_iTempDragOffset_x = oArtworkViewer.g_iTempDragOffset_x < -oArtworkViewer.g_iCompareImage_x / 2 ? -oArtworkViewer.g_iCompareImage_x / 2 : oArtworkViewer.g_iTempDragOffset_x;
		oArtworkViewer.g_iTempDragOffset_y = oArtworkViewer.g_iTempDragOffset_y > oArtworkViewer.g_iCompareImage_y / 2 ? oArtworkViewer.g_iCompareImage_y / 2 : oArtworkViewer.g_iTempDragOffset_y;
		oArtworkViewer.g_iTempDragOffset_y = oArtworkViewer.g_iTempDragOffset_y < -oArtworkViewer.g_iCompareImage_y / 2 ? -oArtworkViewer.g_iCompareImage_y / 2 : oArtworkViewer.g_iTempDragOffset_y;

		// Modify the offset by the zoom amount.
		var iZoomAmount = (oArtworkViewer.g_iTempZoomLevel / oArtworkViewer.g_iZoomLevel);
		oArtworkViewer.g_iTempDragOffset_x *= iZoomAmount;
		oArtworkViewer.g_iTempDragOffset_y *= iZoomAmount;

		// Calculate the zoom and drag offset difference by frame.
		oArtworkViewer.g_iZoomDiff = Math.abs((oArtworkViewer.g_iTempZoomLevel - oArtworkViewer.g_iZoomLevel)) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragXDiff = 0 - ((oArtworkViewer.g_iTempDragOffset_x - oArtworkViewer.g_iDragOffset_x)) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragYDiff = 0 - ((oArtworkViewer.g_iTempDragOffset_y - oArtworkViewer.g_iDragOffset_y)) / oArtworkViewer.g_iAnimateFrames;

		// If we are syncing the zoom across other canvas.
		for (var i = 0; i < oSyncCanvas.length; i++) {
			if (oSyncCanvas[i].g_szCanvasId != oArtworkViewer.g_szCanvasId) {
				oSyncCanvas[i].synchroniseZoomPanProperties(oArtworkViewer);
				oSyncCanvas[i].g_oCanvasZoom.startZoomAnimate();
			}
		}

		// Zoom the canvas.
		oArtworkViewer.g_oCanvasZoom.startZoomAnimate();
	};

	/**
	 * The callback for a scroll zoom out centered event.
	 * @param {object} event - The event.
	 * @param {object} oArtworkViewer - The artwork viewer.
	 * @param {array} oSyncCanvas - Any sync canvas.
	 * @param {int} iBeginPoint_x - The start x point.
	 * @param {int} iBeginPoint_y - The start y point.
	 * @param {int} iEndPoint_x - The end x point.
	 * @param {int} iEndPoint_y - The end y point.
	 */
	CanvasZoom.prototype.scrollZoomOutCenteredCallback = function (event, oArtworkViewer, oSyncCanvas, iBeginPoint_x, iBeginPoint_y, iEndPoint_x, iEndPoint_y) {

		// Calculate the zoom amount.
		oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_iZoomLevel / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier;

		// If we are over our minimum zoom level, set this to the minimum.
		if (oArtworkViewer.g_iTempZoomLevel < oArtworkViewer.g_oCanvasZoom.c_iMinimumZoomLevel) {
			oArtworkViewer.g_iTempZoomLevel = oArtworkViewer.g_oCanvasZoom.c_iMinimumZoomLevel;
		}

		// If we are now fully zoomed out.
		if (oArtworkViewer.g_iTempZoomLevel == 1) {

			// Set the drag offset to the center.
			oArtworkViewer.g_iTempDragOffset_x = 0;
			oArtworkViewer.g_iTempDragOffset_y = 0;
		}
		// If we are not fully zoomed out.
		else {

			// Divide the drag offset by the zoom multiplier to keep the offset the same place.
			oArtworkViewer.g_iTempDragOffset_x = (oArtworkViewer.g_iDragOffset_x / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier);
			oArtworkViewer.g_iTempDragOffset_y = (oArtworkViewer.g_iDragOffset_y / oArtworkViewer.g_oCanvasZoom.g_iZoomMultiplier);

			// Move the drag offset towards the cente based on the zoom level.
			oArtworkViewer.g_iTempDragOffset_x -= oArtworkViewer.g_iTempDragOffset_x / oArtworkViewer.g_iZoomLevel;
			oArtworkViewer.g_iTempDragOffset_y -= oArtworkViewer.g_iTempDragOffset_y / oArtworkViewer.g_iZoomLevel;
		}

		// Calculate the zoom and drag offset difference by frame.
		oArtworkViewer.g_iZoomDiff = Math.abs((oArtworkViewer.g_iTempZoomLevel - oArtworkViewer.g_iZoomLevel)) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragXDiff = 0 - ((oArtworkViewer.g_iTempDragOffset_x - oArtworkViewer.g_iDragOffset_x)) / oArtworkViewer.g_iAnimateFrames;
		oArtworkViewer.g_iDragYDiff = 0 - ((oArtworkViewer.g_iTempDragOffset_y - oArtworkViewer.g_iDragOffset_y)) / oArtworkViewer.g_iAnimateFrames;

		// If we are syncing the zoom across other canvas.
		for (var i = 0; i < oSyncCanvas.length; i++) {
			if (oSyncCanvas[i].g_szCanvasId != oArtworkViewer.g_szCanvasId) {
				oSyncCanvas[i].synchroniseZoomPanProperties(oArtworkViewer);
				oSyncCanvas[i].g_oCanvasZoom.startZoomAnimate();
			}
		}

		// Zoom the canvas.
		oArtworkViewer.g_oCanvasZoom.startZoomAnimate();
	};

	// Add the canvas zoom object to the window.
	window.CanvasZoom = CanvasZoom;

}());